import { useState, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, LifeBuoy, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type Prefill = {
  apartmentId: number | null;
  cleaningJobId: number | null;
  apartmentName: string | null;
  category?: string;
  laundryJobId?: number | null;
  endpoint?: 'cleaner' | 'laundry';
} | null;

const BLANK = { subject: "", category: "general", body: "" };

export default function WorkerTicketDialog({ open, onClose, prefill }: {
  open: boolean;
  onClose: () => void;
  prefill?: Prefill;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const photoInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState(BLANK);
  const [photos, setPhotos] = useState<File[]>([]);

  useEffect(() => {
    if (open) {
      setForm({ ...BLANK, category: prefill?.category ?? "general" });
      setPhotos([]);
    }
  }, [open, prefill]);

  const reset = () => { setForm(BLANK); setPhotos([]); onClose(); };

  const endpoint = prefill?.endpoint ?? 'cleaner';
  const endpointPath = endpoint === 'laundry' ? "/api/laundry/tickets" : "/api/cleaner/tickets";

  const mutation = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("subject", form.subject);
      fd.append("category", form.category);
      if (form.body) fd.append("body", form.body);
      if (prefill?.apartmentId != null) fd.append("apartmentId", String(prefill.apartmentId));
      if (endpoint === 'laundry') {
        if (prefill?.laundryJobId != null) fd.append("laundryJobId", String(prefill.laundryJobId));
      } else {
        if (prefill?.cleaningJobId != null) fd.append("cleaningJobId", String(prefill.cleaningJobId));
      }
      for (const photo of photos) fd.append("photos", photo);
      const res = await fetch(endpointPath, { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) throw new Error("Failed to submit");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [endpointPath] });
      toast({ title: "Ticket submitted" });
      reset();
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    setPhotos(prev => [...prev, ...Array.from(files)].slice(0, 5));
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) reset(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Submit Support Ticket</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {endpoint === 'laundry' && prefill?.laundryJobId != null && (
            <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              <LifeBuoy className="w-3.5 h-3.5 shrink-0" />
              Linked to laundry job #{prefill.laundryJobId}{prefill.apartmentName ? ` · ${prefill.apartmentName}` : ""}
            </div>
          )}
          {endpoint !== 'laundry' && prefill?.cleaningJobId != null && (
            <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              <LifeBuoy className="w-3.5 h-3.5 shrink-0" />
              Linked to job #{prefill.cleaningJobId}{prefill.apartmentName ? ` · ${prefill.apartmentName}` : ""}
            </div>
          )}
          <div>
            <Label>Subject</Label>
            <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Brief description" />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="cleaning_issue">Cleaning Issue</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="access_issue">Access Issue</SelectItem>
                <SelectItem value="supply_request">Supply Request</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Details</Label>
            <Textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} placeholder="Describe the issue…" rows={4} />
          </div>
          <div>
            <Label>Photos (optional)</Label>
            <div className="space-y-2">
              <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={e => addPhotos(e.target.files)} />
              <Button type="button" variant="outline" size="sm" onClick={() => photoInputRef.current?.click()}>
                <Camera className="w-4 h-4 mr-1" />
                Add Photos{photos.length > 0 ? ` (${photos.length}/5)` : ""}
              </Button>
              {photos.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {photos.map((file, idx) => (
                    <div key={idx} className="relative group">
                      <img src={URL.createObjectURL(file)} alt={`Photo ${idx + 1}`} className="w-16 h-16 object-cover rounded-md border" />
                      <button type="button" onClick={() => setPhotos(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={reset}>Cancel</Button>
            <Button onClick={() => mutation.mutate()} disabled={!form.subject.trim() || mutation.isPending}>
              {mutation.isPending ? "Submitting…" : "Submit"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
